<template>
	<ValidationForm
		ref="validationForm"
		#default="{ handleSubmit }"
	>
		<ViewLayout>
			<template #header-title>
				{{ isEdit ? 'Edit' : 'Create' }} Service
			</template>
			<template #content>
				<SectionCard>
					<template #title>
						<span class="text-subtitle-1 font-weight-medium">
							Basic Details
						</span>
					</template>
					<template #body>
						<div class="cassie-vertical-md">
							<v-row dense>
								<v-col cols="6">
									<TextField
										v-model="cookieScript.scriptName"
										:disabled="!userFullPermissions"
										label="Name *"
										:rules="{required: true, max: 120}"
									/>
								</v-col>
								<v-col cols="6">
									<Dropdown
										v-model="cookieScript.brandId"
										:disabled="!userFullPermissions || isEdit || accessedViaCookieBannerConfiguration || !clientMultiBranded || singleBrand"
										:items="brandItems"
										custom-sort
										:label="'Brand *' | useLabels"
										searchable
										:rules="{ required: clientMultiBranded}"
										@change="updateParentCookieCategory(cookieScript.type)"
									/>
								</v-col>
							</v-row>
							<v-row dense>
								<v-col cols="6">
									<Dropdown
										v-model="cookieScript.type"
										label="Type *"
										:disabled="
											isEdit ||
												selectedCategoryIsStrictlyNecessary ||
												!userFullPermissions
										"
										:items="typeItems"
										rules="required"
										@change="updateParentCookieCategory($event)"
									/>
								</v-col>
								<v-col cols="6">
									<Dropdown
										v-model="cookieScript.parentCookieCategoryId"
										label="Service Category *"
										:items="categoryItems"
										searchable
										class="cassie-input-width-xl"
										item-disabled="disabled"
										:disabled="
											isEdit ||
												!userFullPermissions ||
												accessedViaCookieBannerConfiguration ||
												cookieScript.type === 'Essential' ||
												cookieScript.channelGuid"
										rules="required"
									/>
								</v-col>
							</v-row>
						</div>
					</template>
				</SectionCard>
				<SectionCard
					collapsible
					:collapsed="true"
				>
					<template #title>
						<span class="text-subtitle-1 font-weight-medium">
							Additional Details
						</span>
					</template>
					<template #body>
						<v-row dense>
							<v-col cols="6">
								<TextField
									v-model="cookieScript.fullLegalName"
									:disabled="!userFullPermissions"
									label="Company Name"
									:rules="{max: 100}"
									tooltip-text="This should be the legal name of the company that provides this service."
								/>
							</v-col>
							<v-col cols="6">
								<TextField
									v-model="cookieScript.address1"
									:disabled="!userFullPermissions"
									label="Address Line 1"
									:rules="{max: 100}"
									tooltip-text="This should be the address of the company providing the service."
								/>
							</v-col>
							<v-col cols="6">
								<TextField
									v-model="cookieScript.address2"
									:disabled="!userFullPermissions"
									label="Address Line 2"
									:rules="{max: 100}"
									tooltip-text="This should be the address of the company providing the service."
								/>
							</v-col>
							<v-col cols="6">
								<TextField
									v-model="cookieScript.address3"
									:disabled="!userFullPermissions"
									label="Address Line 3"
									:rules="{max: 100}"
									tooltip-text="This should be the address of the company providing the service."
								/>
							</v-col>
							<v-col cols="6">
								<TextField
									v-model="cookieScript.address4"
									:disabled="!userFullPermissions"
									label="Address Line 4"
									:rules="{max: 100}"
									tooltip-text="This should be the address of the company providing the service."
								/>
							</v-col>
							<v-col cols="6">
								<TextField
									v-model="cookieScript.country"
									:disabled="!userFullPermissions"
									label="Country"
									:rules="{max: 50}"
									tooltip-text="This should be the country in which the company's address is in."
								/>
							</v-col>
						</v-row>
						<v-row dense>
							<v-col cols="6">
								<Dropdown
									v-model="selectedDataProcessingCategories"
									label="Processed Data Categories"
									:items="availableDataProcessingCategories"
									item-disabled="disabled"
									:disabled="!userFullPermissions"
									multiple
									searchable
									tooltip-text="Select the categories of data that the service may use."
								/>
							</v-col>
							<v-col cols="6">
								<Dropdown
									v-model="selectedDataProcessingPurposes"
									label="Data Processing Purposes"
									:items="availableDataProcessingPurposes"
									item-disabled="disabled"
									:disabled="!userFullPermissions"
									multiple
									searchable
									tooltip-text="Select the purposes that the service may use the data collected for."
								/>
							</v-col>
						</v-row>
						<v-row dense>
							<v-col cols="12">
								<TextField
									v-model="cookieScript.servicePrivacyPolicyUrl"
									label="Service Privacy Policy URL"
									class="cassie-input-width-xl"
									:disabled="!userFullPermissions"
									tooltip-text="The URL of the service's privacy policy, if available.  This will be for the default language, alternative language URLs can be set in the translations for this service."
									rules="isLink"
								/>
							</v-col>
						</v-row>
					</template>
				</SectionCard>
				<SectionCard>
					<template #title>
						<span class="text-subtitle-1 font-weight-medium">
							Service Settings
						</span>
					</template>
					<template #body>
						<v-row dense>
							<v-col cols="6">
								<Toggle
									:value.sync="cookieScript.isRunFirst"
									label="Run First?"
									tooltip-text="Enable if you wish for this script to be executed before the Essential scripts (if the user opts in)."
									:disabled="!userFullPermissions"
								/>
							</v-col>
							<v-col cols="6">
								<Toggle
									:value.sync="cookieScript.gpcEnabled"
									label="Global Privacy Control"
									tooltip-text="Enable if you wish for this script to detect GPC signals from user's browsers."
									:disabled="!userFullPermissions"
								/>
							</v-col>
						</v-row>
					</template>
				</SectionCard>
				<SectionCard v-if="enableTransactionTypes">
					<template #title>
						<span class="text-subtitle-1 font-weight-medium">
							Service Transaction Settings
						</span>
					</template>
					<template #body>
						<v-row
							dense
						>
							<v-col cols="6">
								<Dropdown
									v-model="cookieScript.transactionTypeId"
									label="Transaction Type *"
									:items="transactionTypeItems"
									class="cassie-input-width-xl"
									item-disabled="disabled"
									:disabled="!userFullPermissions"
									rules="required"
								/>
							</v-col>
						</v-row>
						<v-row dense>
							<v-col cols="6">
								<Toggle
									:value.sync="overrideSecurityLevel"
									label="Override Security Level?"
									:tooltip-text="securityLevelTooltip"
									:disabled="!userFullPermissions"
								/>
							</v-col>
							<v-col cols="6">
								<Dropdown
									v-if="overrideSecurityLevel"
									v-model="cookieScript.overrideSecurityLevelId"
									label="Security Level *"
									:items="overrideSecurityLevelItems"
									class="cassie-input-width-xl"
									item-disabled="disabled"
									:disabled="!userFullPermissions"
									rules="required"
								/>
							</v-col>
						</v-row>
						<v-row dense>
							<v-col cols="6">
								<Toggle
									:value.sync="copyFromDeviceToDataSubject"
									label="Copy from Device to Data Subject?"
									tooltip-text="Enabling the ability to Copy from Device to Data Subject allows for Consent & Preferences captured at a Device Level to be copied over to any associated Person Records."
									:disabled="!userFullPermissions"
								/>
							</v-col>
							<v-col cols="6">
								<Dropdown
									v-if="copyFromDeviceToDataSubject"
									v-model="cookieScript.deviceCopyActionId"
									label="Copy Action *"
									:items="copyActionItems"
									class="cassie-input-width-xl"
									item-disabled="disabled"
									:disabled="!userFullPermissions"
									rules="required"
								/>
							</v-col>
						</v-row>
					</template>
				</SectionCard>
				<SectionCard>
					<template #title>
						<span class="text-subtitle-1 font-weight-medium">
							Google Consent Mode
						</span>
					</template>
					<template #body>
						<v-row dense>
							<v-col cols="6">
								<Toggle
									:value.sync="cookieScript.gtmEnabled"
									label="Google Consent Mode integration"
									tooltip-text="Enabling Google Consent mode integration will append a google tag manager consent property to the service. This will be referenced within the Cassie CMP Tag Template within Google Tag manager."
									:disabled="!userFullPermissions"
									@update:value="resetGtmConsentType($event)"
								/>
							</v-col>
							<v-col cols="6">
								<Dropdown
									v-model="cookieScript.gtmConsentTypeId"
									label="Consent Type"
									:items="gtmConsentTypes"
									:disabled="!userFullPermissions || !cookieScript.gtmEnabled"
									:rules="validateGtmConsentType"
									tooltip-text="Google Tag Manager consent types can be used to control tag firing in Google Tag Manager, selecting a consent type will attribute that consent type to the service."
									custom-sort
									:html-error-message="true"
								/>
							</v-col>
						</v-row>
					</template>
				</SectionCard>
				<SectionCard>
					<template #title>
						<span class="text-subtitle-1 font-weight-medium">
							Deployment
						</span>
					</template>
					<template #body>
						<v-row dense>
							<v-col cols="12">
								<Froala
									v-if="userFullPermissions"
									v-model="cookieScript.displayText"
									title="Display Text *"
									:rules="{required: true, max: 1000}"
								/>
								<div
									v-else
									v-dompurify-html="cookieScript.displayText"
									class="mt-2 cassie-disabled-textarea rounded-lg"
								/>
							</v-col>
						</v-row>
						<v-row dense>
							<v-col cols="12">
								<TextField
									v-model="cookieScript.cookieInfoUrl"
									label="Information URL"
									class="cassie-input-width-xl"
									:disabled="!userFullPermissions"
									tooltip-text="Add a link to a web page that contains more information about this script."
									rules="isLink"
								/>
							</v-col>
						</v-row>
						<v-row dense>
							<v-col cols="12">
								<OptionSelector
									v-if="userFullPermissions"
									:selected="cookieScript.storageDetails"
									entity-type="item"
									is-text
									:columns="[
										{ key: 'name', label: 'Storage Name', width: '25%', tooltip: 'The name of the item, usually the name of the cookie.'},
										{ key: 'type', label: 'Storage Type', width: '25%', tooltip: 'The type of storage being used, usually cookie.' },
										{ key: 'retentionPeriod', label: 'Retention Period', width: '25%', tooltip: 'The amount of time this storage item is held for, usually expressed as number of minutes or days.' }
									]"
									@update:selected="updateCookiesInScript($event)"
								/>
							</v-col>
						</v-row>
						<v-row dense>
							<v-col cols="6">
								<TextField
									v-model.number="cookieScript.scriptExpiryInDays"
									type="number"
									:disabled="!userFullPermissions"
									label="Script Expiry (in days)"
									tooltip-text="Specify how long the cookie(s) will be persisted for in the user's browser (in days)."
									rules="numeric"
								/>
							</v-col>
							<v-col cols="6">
								<TextField
									v-model.number="cookieScript.consentExpiryInDays"
									type="number"
									:disabled="!userFullPermissions"
									label="Consent Expiry (in days)"
									tooltip-text="Specify how long the consent given for this service is valid for (in days)."
									rules="numeric"
								/>
							</v-col>
						</v-row>
						<v-row dense>
							<v-col :cols="cookieScript.scriptPlacementTypeId === BODY_SCRIPT_PLACEMENT ? 6 : 12">
								<Dropdown
									v-model="cookieScript.scriptPlacementTypeId"
									label="Script Placement"
									:disabled="!userFullPermissions"
									custom-sort
									:items="placementAreaItems"
								/>
							</v-col>
							<v-col
								v-if="bodyScriptPlacement"
								cols="6"
							>
								<TextField
									v-model="cookieScript.optInDivBody"
									:disabled="!userFullPermissions"
									label="Body Opt-in div id"
									tooltip-text="The Id of the HTML div where the opt-in script will be appended to."
								/>
							</v-col>
						</v-row>
						<v-row dense>
							<v-col cols="12">
								<Textarea
									v-if="headScriptPlacement"
									v-model="cookieScript.optInScript"
									:disabled="!userFullPermissions"
									no-resize
									label="Head Opt-in Script"
									tooltip-text="The script that will be appended to the head of the page and executed, if the user opts-in."
								/>
								<Textarea
									v-else
									v-model="cookieScript.optInScriptBody"
									:disabled="!userFullPermissions"
									no-resize
									label="Body Opt-in Script"
									tooltip-text="The script that will be executed, if the user opts-in."
								/>
							</v-col>
						</v-row>
						<v-row
							v-if="bodyScriptPlacement"
							dense
						>
							<v-col cols="12">
								<TextField
									v-model="cookieScript.optOutDivBody"
									:disabled="!userFullPermissions"
									label="Body Opt-out div id"
									tooltip-text="The Id of the HTML div where the opt-out script will be appended to."
								/>
							</v-col>
						</v-row>
						<v-row>
							<v-col
								v-if="headScriptPlacement"
								cols="12"
							>
								<Textarea
									v-model="cookieScript.optOutScript"
									:disabled="!userFullPermissions"
									no-resize
									label="Head Opt-out Script"
									tooltip-text="The script that will be appended to the head of the page and executed, if the user opts-out."
								/>
							</v-col>
							<v-col
								v-else
								cols="12"
							>
								<Textarea
									v-model="cookieScript.optOutScriptBody"
									:disabled="!userFullPermissions"
									no-resize
									label="Body Opt-out Script"
									tooltip-text="The script that will be executed, if the user opts-out."
								/>
							</v-col>
						</v-row>
					</template>
				</SectionCard>
			</template>
			<template #footer>
				<PageActionRow>
					<template #actions>
						<SecondaryActionButton @click="backToList">
							{{ accessedViaCollectionTemplate ? 'Back to Collection Template' : 'Back to Overview' }}
						</SecondaryActionButton>
						<v-spacer />
						<PrimaryActionButton
							v-if="userFullPermissions"
							@click="handleSubmit(submitForm)"
						>
							{{ !isEdit ? 'Create' : 'Save' }}
						</PrimaryActionButton>
					</template>
				</PageActionRow>
			</template>
		</ViewLayout>
		<CookieScriptRequestNewConsentsModal
			v-if="showRequestNewConsents"
			:associated-consent-banners="cookieScript.associatedConsentBanners"
			@close="showRequestNewConsents = false"
			@no-new-consents="submitNoNewConsents"
			@request-new-consents="submitWithNewConsents"
		/>
	</ValidationForm>
</template>

<script>
/* eslint-disable max-lines */
import { mapGetters } from 'vuex'
import SectionCard from '../../../../../../../shared/components/section-card.vue'
import ViewLayout from '../../../../../../../shared/layouts/view-layout.vue'
import ValidationForm from '../../../../../../../shared/components/validation-form.vue'
import TextField from '../../../../../../../shared/components/text-field.vue'
import Dropdown from '../../../../../../../shared/components/dropdown.vue'
import Textarea from '../../../../../../../shared/components/textarea.vue'
import Toggle from '../../../../../../../shared/components/toggle.vue'
import OptionSelector from '../../../../../../../shared/components/option-selector.vue'
import Froala from '../../../../shared/froala-editor.vue'
import SecondaryActionButton from '../../../../../../../shared/components/secondary-action-button.vue'
import PrimaryActionButton from '../../../../../../../shared/components/primary-action-button.vue'
import PageActionRow from '../../../../../../../shared/components/page-action-row.vue'
import { getAssignedLanguages } from '../../../../../../../shared/utils/api/languages.js'
import { singleBrand, defaultBrandInSelect, clientMultiBranded, brandOptions, UNIVERSAL_BRAND, UNIVERSAL_BRAND_ITEM } from '../../../../../../../shared/state/brands.js'
import { createCookieScript, editCookieScript, getGtmConsentTypes, getCookieScript, getCookieScripts } from '../../../../../../../shared/utils/api/cookies/cookie-scripts.js'
import { getCookieCategories } from '../../../../../../../shared/utils/api/cookies/cookie-categories.js'
import { showSnackbar } from '../../../../../../../shared/state/snackbar.js'
import { scriptTagDefault } from './cookie-scripts-defaults.js'
import { enableTransactionTypes } from '../../../../../../../shared/state/config-keys.js'
import { getTransactionTypes } from '../../../../../../../shared/utils/api/transaction-types.js'
import { getDataProcesses } from '../../../../../../../shared/utils/api/data-processes.js'
import { COOKIE_SCRIPTS } from '../../../../../router/route-names.js'
import { CAN_CREATE_UPDATE_COOKIE_SCRIPTS } from '../../../../../../../shared/permissions/admin-portal-permissions.js'
import { getCookieCollectionTemplates } from '../../../../../../../shared/utils/api/cookies/cookie-collection-templates.js'
import CookieScriptRequestNewConsentsModal from './cookie-script-request-new-consents-modal.vue'
export default {
	components: {
		ViewLayout,
		ValidationForm,
		SectionCard,
		TextField,
		Dropdown,
		Textarea,
		Toggle,
		OptionSelector,
		Froala,
		SecondaryActionButton,
		PrimaryActionButton,
		PageActionRow,
		CookieScriptRequestNewConsentsModal
	},
	props: {
		cookieScriptChannelId: [String, Number],
		cookieCategoryId: Number,
		accessedViaCookieBannerConfiguration: {
			type: Boolean,
			default: false
		},
		cookieCollectionBrandId: Number,
		cookieScriptToEdit: Object,
		accessedViaCollectionTemplate: {
			type: Boolean,
			default: false
		}
	},
	setup () {
		return {
			brandOptions,
			showSnackbar,
			clientMultiBranded,
			singleBrand,
			defaultBrandInSelect,
			enableTransactionTypes
		}
	},
	data () {
		const HEAD_SCRIPT_PLACEMENT = 1
		const BODY_SCRIPT_PLACEMENT = 2
		const ESSENTIAL_SCRIPT_TYPE = 'Essential'
		const DEFAULT_LANGUAGEID = 0
		const DATA_PROCESSING_CATEGORY_TYPE = 1
		const DATA_PROCESSING_PURPOSE_TYPE = 2
		const SERVICE_URL_PRIVACY_URL_TYPE = 1
		return {
			cookieScript: {
				scriptName: null,
				brandId: defaultBrandInSelect.value,
				type: null,
				parentCookieCategoryId: null,
				isRunFirst: false,
				gpcEnabled: false,
				displayText: null,
				cookieInfoUrl: null,
				browserCookieNames: [],
				scriptExpiryInDays: 0,
				consentExpiryInDays: 0,
				scriptPlacementTypeId: HEAD_SCRIPT_PLACEMENT,
				optInDivBody: null,
				optInScript: scriptTagDefault,
				optInScriptBody: scriptTagDefault,
				optOutDivBody: null,
				optOutScript: scriptTagDefault,
				optOutScriptBody: scriptTagDefault,
				gtmEnabled: false,
				gtmConsentTypeId: null,
				storageDetails: [],
				fullLegalName: null,
				address1: null,
				address2: null,
				address3: null,
				address4: null,
				country: null,
				servicePrivacyPolicyUrl: null,
				requestNewConsents: false
			},
			assignedLanguages: [],
			cookieCategories: [],
			transactionTypes: [],
			securityLevels: [],
			channelDeviceCopyActions: [],
			BODY_SCRIPT_PLACEMENT,
			HEAD_SCRIPT_PLACEMENT,
			ESSENTIAL_SCRIPT_TYPE,
			selectedCategoryIsStrictlyNecessary: false,
			gtmConsentTypes: [],
			overrideSecurityLevel: false,
			copyFromDeviceToDataSubject: false,
			dataProcesses: [],
			DEFAULT_LANGUAGEID,
			DATA_PROCESSING_CATEGORY_TYPE,
			DATA_PROCESSING_PURPOSE_TYPE,
			SERVICE_URL_PRIVACY_URL_TYPE,
			selectedDataProcessingCategories: [],
			selectedDataProcessingPurposes: [],
			gtmCookieScripts: [],
			cookieCollectionTemplates: [],
			showRequestNewConsents: false
		}
	},
	computed: {
		...mapGetters('auth', ['productAreaPermission']),
		isEdit () {
			return !!(this.cookieScriptToEdit || this.cookieScriptChannelId)
		},
		brandItems () {
			// ensures when viewing universal object it displays in dropdown
			const containsUniversal = brandOptions.value.map(brand => {
				return brand.value
			}).includes(UNIVERSAL_BRAND)
			if (containsUniversal) {
				return brandOptions.value
			} else {
				return !this.isEdit
					? brandOptions.value
					: [
						UNIVERSAL_BRAND_ITEM,
						...brandOptions.value
					]
			}
		},
		validateGtmConsentType () {
			return {
				validateGtmConsentTypeInService: {
					serviceId: this.cookieScript.id,
					gtmUsageInAssociatedCollections: this.gtmUsageInAssociatedCollections,
					gtmConsentTypes: this.gtmConsentTypes
				}
			}
		},
		typeItems () {
			let types = [
				'Essential', 'Legitimate Interest', 'User Consent'
			]
			if (this.accessedViaCookieBannerConfiguration && !this.selectedCategoryIsStrictlyNecessary) {
				types = types.filter(type => type !== 'Essential')
			}
			return types
		},
		placementAreaItems () {
			return [
				{
					text: '<head>',
					value: this.HEAD_SCRIPT_PLACEMENT
				},
				{
					text: '<body>',
					value: this.BODY_SCRIPT_PLACEMENT
				}
			]
		},
		headScriptPlacement () {
			return this.cookieScript.scriptPlacementTypeId === this.HEAD_SCRIPT_PLACEMENT
		},
		bodyScriptPlacement () {
			return this.cookieScript.scriptPlacementTypeId === this.BODY_SCRIPT_PLACEMENT
		},
		categoryItems () {
			const current = this.cookieScript.parentCookieCategoryId
			const categories = this.cookieCategories.filter(cat => !cat.channelGuid || cat.id === current).map(({ id, categoryName, isStrictlyCategory, brandId, brandName }) => ({
				text: brandName != null ? categoryName + ' - ' + brandName : categoryName,
				value: id,
				isStrictlyCategory: isStrictlyCategory,
				brandId: brandId,
				brandName: brandName
			}))
			const categoriesByBrand = this.cookieScript.brandId !== 0 ? categories.filter(({ brandId }) => brandId === this.cookieScript.brandId || brandId === 0) : categories
			if (this.cookieScript.type === this.ESSENTIAL_SCRIPT_TYPE) {
				return categoriesByBrand
			} else {
				return categoriesByBrand.filter(({ isStrictlyCategory }) => !isStrictlyCategory)
			}
		},
		transactionTypeItems () {
			const transactionTypes = this.transactionTypes?.map(({ id, name, securityLevelName }) => ({
				text: name,
				value: id,
				accessible: securityLevelName.toLowerCase() === 'accessible'
			}))
			return transactionTypes
		},
		transactionTypeAccessible () {
			const transactionType = this.transactionTypes.find(type => type.id === this.cookieScript.transactionTypeId)
			return (transactionType?.securityLevelName.toLowerCase() || '') === 'accessible'
		},
		overrideSecurityLevelItems () {
			return this.securityLevels?.map(({ id, name }) => ({
				text: name,
				value: id
			})) || []
		},
		copyActionItems () {
			return this.channelDeviceCopyActions?.map(({ channelDeviceCopyActionId, actionName }) => ({
				text: actionName,
				value: channelDeviceCopyActionId
			})) || []
		},
		selectedSecurityLevel () {
			const transactionType = this.transactionTypes?.find(type => type.id === this.cookieScript.transactionTypeId)
			return transactionType?.securityLevelName
		},
		securityLevelTooltip () {
			if (!this.selectedSecurityLevel) {
				return 'Enabling this toggle will allow the Security Level to be managed within the Channel.'
			} else {
				return `This Transaction Type's Security Level is set to ${this.selectedSecurityLevel}. Enabling this toggle will allow the Security Level to be managed within the Channel.`
			}
		},
		availableDataProcessingCategories () {
			return this.dataProcesses
				.filter(process => process.processType === this.DATA_PROCESSING_CATEGORY_TYPE)
				.map(({ processId, processName }) => ({
					text: processName,
					value: processId
				}))
		},
		availableDataProcessingPurposes () {
			return this.dataProcesses
				.filter(process => process.processType === this.DATA_PROCESSING_PURPOSE_TYPE)
				.map(({ processId, processName }) => ({
					text: processName,
					value: processId
				}))
		},
		defaultLanguageLookupId () {
			return this.assignedLanguages.find(({ languageID }) => languageID === this.DEFAULT_LANGUAGEID)?.languageLookupID
		},
		userFullPermissions () {
			return this.productAreaPermission(CAN_CREATE_UPDATE_COOKIE_SCRIPTS) && !this.cookieScript?.channelGuid
		},
		gtmUsageInAssociatedCollections () {
			const currentScriptId = this.cookieScript?.id
			if (!currentScriptId) {
				return []
			}

			return this.cookieCollectionTemplates
				.filter(template => template.cookieScripts.some(script => script.id === currentScriptId))
				.map(template => ({
					templateId: template.id,
					templateName: template.name,
					brandName: template.brandName,
					gtmScripts: this.gtmCookieScripts.filter(gtmScript =>
						template.cookieScripts.some(script => script.id === gtmScript.id && gtmScript.gtmEnabled && gtmScript.gtmConsentTypeId))
						.map(gtmScript => ({
							scriptId: gtmScript.id,
							scriptName: gtmScript.cookieScriptName,
							consentTypeId: gtmScript.gtmConsentTypeId,
							consentTypeName: this.gtmConsentTypes.find(type => type.value === gtmScript.gtmConsentTypeId)?.text || '',
							categoryId: gtmScript.cookieCategoryId,
							categoryName: gtmScript.cookieCategoryName,
							brandId: gtmScript.brandId,
							brandName: gtmScript.brandName
						}))
				}))
		},
		hasAssociatedConsentBanners () {
			return Array.isArray(this.cookieScript?.associatedConsentBanners) && this.cookieScript.associatedConsentBanners.length > 0
		}
	},
	async created () {
		await Promise.all([
			this.loadCookieCategories(),
			this.loadCookieScripts(),
			this.loadTransactionTypes(),
			this.getGtmConsentTypes(),
			this.loadDataProcesses(this.DEFAULT_LANGUAGEID),
			this.loadLanguages(),
			this.loadCookieCollectionTemplates()
		])

		// Handle editing from props (collection template modal)
		if (this.cookieScriptToEdit) {
			this.populateFromEditData(this.cookieScriptToEdit)
		} else if (this.cookieScriptChannelId) {
			// Handle editing from route params (standalone page)
			await this.loadCookieScriptToEdit(this.cookieScriptChannelId).then(() => {
				this.$refs.validationForm.reset()
			})
		}

		if ((this.accessedViaCookieBannerConfiguration || this.accessedViaCollectionTemplate) && !this.isEdit) {
			const cookieCategoryIsStrictlyNecessary = this.cookieCategories.find(({ id }) => id === this.cookieCategoryId)?.isStrictlyCategory
			this.selectedCategoryIsStrictlyNecessary = cookieCategoryIsStrictlyNecessary
			if (cookieCategoryIsStrictlyNecessary) {
				this.cookieScript.type = this.ESSENTIAL_SCRIPT_TYPE
			}
			this.cookieScript.parentCookieCategoryId = this.cookieCategoryId
			this.cookieScript.brandId = this.cookieCollectionBrandId
		}

		this.overrideSecurityLevel = this.cookieScript.overrideSecurityLevelId !== null && this.cookieScript.overrideSecurityLevelId !== undefined
		this.copyFromDeviceToDataSubject = this.cookieScript.deviceCopyActionId !== null && this.cookieScript.deviceCopyActionId !== undefined

		if (this.cookieScript.optInScript === '' || !this.cookieScript.optInScript) this.cookieScript.optInScript = scriptTagDefault
		if (this.cookieScript.optInScriptBody === '' || !this.cookieScript.optInScriptBody) this.cookieScript.optInScriptBody = scriptTagDefault
		if (this.cookieScript.optOutScript === '' || !this.cookieScript.optOutScript) this.cookieScript.optOutScript = scriptTagDefault
		if (this.cookieScript.optOutScriptBody === '' || !this.cookieScript.optOutScriptBody) this.cookieScript.optOutScriptBody = scriptTagDefault
	},
	methods: {
		async loadLanguages () {
			const { data: assignedLanguages } = await getAssignedLanguages()
			this.assignedLanguages = assignedLanguages
		},
		async loadCookieCategories () {
			const { cookieCategories } = await getCookieCategories()
			this.cookieCategories = cookieCategories
		},
		async loadCookieScripts () {
			this.gtmCookieScripts = (await getCookieScripts()).cookieScripts
				.filter(({ gtmEnabled, gtmConsentTypeId }) => gtmEnabled && gtmConsentTypeId)
		},
		async loadTransactionTypes () {
			const { data: { transactionTypes, securityLevels, channelDeviceCopyActions } } = await getTransactionTypes()
			this.transactionTypes = transactionTypes
			this.securityLevels = securityLevels
			this.channelDeviceCopyActions = channelDeviceCopyActions
		},
		async loadCookieScriptToEdit (cookieScriptChannelId) {
			const cookieScriptToEdit = await getCookieScript(cookieScriptChannelId)
			if (cookieScriptToEdit) {
				this.selectedDataProcessingCategories = cookieScriptToEdit.dataProcesses
					? cookieScriptToEdit.dataProcesses
						.filter(process => process.dataProcessTypeId === this.DATA_PROCESSING_CATEGORY_TYPE)
						.map(process => process.dpid)
					: []
				this.selectedDataProcessingPurposes = cookieScriptToEdit.dataProcesses
					? cookieScriptToEdit.dataProcesses
						.filter(process => process.dataProcessTypeId === this.DATA_PROCESSING_PURPOSE_TYPE)
						.map(process => process.dpid)
					: []
				const servicePrivacyPolicyUrl = cookieScriptToEdit.serviceUrls && cookieScriptToEdit.serviceUrls.length
					? cookieScriptToEdit.serviceUrls.find(url => url.typeId === this.SERVICE_URL_PRIVACY_URL_TYPE)?.url
					: null
				this.cookieScript = {
					...cookieScriptToEdit,
					servicePrivacyPolicyUrl
				}
				if (typeof this.cookieScript.browserCookieNames === 'string') {
					this.cookieScript.browserCookieNames = this.cookieScript.browserCookieNames.split(',')
				}
			}
		},
		populateFromEditData (cookieScriptToEdit) {
			this.selectedDataProcessingCategories = cookieScriptToEdit.dataProcesses
				? cookieScriptToEdit.dataProcesses
					.filter(process => process.dataProcessTypeId === this.DATA_PROCESSING_CATEGORY_TYPE)
					.map(process => process.dpid)
				: []
			this.selectedDataProcessingPurposes = cookieScriptToEdit.dataProcesses
				? cookieScriptToEdit.dataProcesses
					.filter(process => process.dataProcessTypeId === this.DATA_PROCESSING_PURPOSE_TYPE)
					.map(process => process.dpid)
				: []
			const servicePrivacyPolicyUrl = cookieScriptToEdit.serviceUrls && cookieScriptToEdit.serviceUrls.length
				? cookieScriptToEdit.serviceUrls.find(url => url.typeId === this.SERVICE_URL_PRIVACY_URL_TYPE)?.url
				: null
			this.cookieScript = {
				...cookieScriptToEdit,
				servicePrivacyPolicyUrl
			}
			if (typeof this.cookieScript.browserCookieNames === 'string') {
				this.cookieScript.browserCookieNames = this.cookieScript.browserCookieNames.split(',')
			}
		},
		async getGtmConsentTypes () {
			const { gtmConsentTypes } = await getGtmConsentTypes()
			this.gtmConsentTypes = gtmConsentTypes
		},
		async loadDataProcesses (languageId) {
			const { data } = await getDataProcesses(languageId)
			if (data && data[0] && data[0].dataProcesses) {
				this.dataProcesses = data[0].dataProcesses.map(process => ({
					processId: process.dpid,
					processName: process.dataProcessName,
					processType: process.dataProcessTypeID
				}))
			}
		},
		resetGtmConsentType (gtmEnabled) {
			if (!gtmEnabled) {
				this.cookieScript.gtmConsentTypeId = null
			}
		},
		updateParentCookieCategory (value) {
			const selectedBrandId = this.cookieScript.brandId ?? 0
			if (value === this.ESSENTIAL_SCRIPT_TYPE) {
				this.cookieScript.parentCookieCategoryId = this.cookieCategories.find(({ isStrictlyCategory, brandId }) => isStrictlyCategory && brandId === selectedBrandId)?.id
			}
		},
		updateCookiesInScript (value) {
			this.cookieScript.storageDetails = value
			this.cookieScript.browserCookieNames = value?.map(item => item?.name).filter(name => name) || []
		},
		backToList () {
			// Check if we're in collection template modal mode
			if (this.accessedViaCollectionTemplate) {
				this.$emit('close')
				return
			}

			// Check if we should return to a specific page
			if (this.$route.params.returnTo) {
				this.$router.push({
					name: this.$route.params.returnTo,
					params: this.$route.params.returnParams || {},
					query: this.$route.params.returnQuery || {}
				})
			} else {
				this.$router.push({ name: COOKIE_SCRIPTS })
			}
		},
		async submit () {
			const cookieScript = { ...this.cookieScript }
			delete cookieScript.associatedConsentBanners
			cookieScript.overrideSecurityLevelId = this.overrideSecurityLevel ? cookieScript.overrideSecurityLevelId : null
			cookieScript.deviceCopyActionId = this.copyFromDeviceToDataSubject ? cookieScript.deviceCopyActionId : null
			cookieScript.scriptDescription = cookieScript.scriptName
			cookieScript.browserCookieNames = this.cookieScript.browserCookieNames.join(',')
			cookieScript.isStrictlyNecessary = cookieScript.type === 'Essential'
			cookieScript.isLegitimateInterest = cookieScript.type === 'Legitimate Interest'

			if (cookieScript.optInScript === scriptTagDefault) cookieScript.optInScript = ''
			if (cookieScript.optInScriptBody === scriptTagDefault) cookieScript.optInScriptBody = ''
			if (cookieScript.optOutScript === scriptTagDefault) cookieScript.optOutScript = ''
			if (cookieScript.optOutScriptBody === scriptTagDefault) cookieScript.optOutScriptBody = ''

			if (this.cookieScript.servicePrivacyPolicyUrl && this.defaultLanguageLookupId) {
				cookieScript.serviceUrls = [
					{
						typeId: this.SERVICE_URL_PRIVACY_URL_TYPE,
						url: this.cookieScript.servicePrivacyPolicyUrl,
						languageLookupId: this.defaultLanguageLookupId
					}
				]
			} else {
				cookieScript.serviceUrls = []
			}
			cookieScript.dataProcesses = [
				...this.selectedDataProcessingCategories,
				...this.selectedDataProcessingPurposes
			]
			cookieScript.serviceDetails = {
				fullLegalName: this.cookieScript.fullLegalName,
				address1: this.cookieScript.address1,
				address2: this.cookieScript.address2,
				address3: this.cookieScript.address3,
				address4: this.cookieScript.address4,
				country: this.cookieScript.country
			}

			try {
				let newScriptId = null
				if (!this.isEdit) {
					const result = await createCookieScript(cookieScript)
					newScriptId = result && result.data ? result.data.newCookieScriptId : null
					showSnackbar('Service created successfully')
				} else {
					await editCookieScript(cookieScript)
					// For editing, we don't need a new ID since the service already exists
					newScriptId = this.cookieScript.id || (this.cookieScriptToEdit && this.cookieScriptToEdit.id)
					showSnackbar('Service updated successfully')
				}				// Handle different return patterns
				if (this.accessedViaCollectionTemplate) {
					// Modal pattern - emit to parent collection template
					this.$emit('submit', {
						id: newScriptId,
						categoryId: this.cookieCategoryId
					})
				} else if (this.$route.params.returnTo) {
					// Router pattern - go back to specific page
					if (newScriptId && this.accessedViaCookieBannerConfiguration) {
						// Store the new script ID in sessionStorage so the parent component can retrieve it
						sessionStorage.setItem('newCookieScriptId', newScriptId)
					}

					this.$router.push({
						name: this.$route.params.returnTo,
						params: this.$route.params.returnParams || {},
						query: this.$route.params.returnQuery || {}
					})
				} else {
					this.$router.push({ name: COOKIE_SCRIPTS })
				}
			} catch (error) {
				showSnackbar({ text: 'Error saving Service: ' + (error.message || 'Unknown error'), color: 'red' })
			}
		},
		async loadCookieCollectionTemplates () {
			const { cookieCollectionTemplates } = await getCookieCollectionTemplates()
			this.cookieCollectionTemplates = cookieCollectionTemplates
		},
		submitForm () {
			if (this.isEdit && this.hasAssociatedConsentBanners) {
				this.showRequestNewConsents = true
			} else {
				this.submit()
			}
		},
		submitNoNewConsents () {
			this.showRequestNewConsents = false
			this.cookieScript.requestNewConsents = false
			this.submit()
		},
		submitWithNewConsents () {
			this.showRequestNewConsents = false
			this.cookieScript.requestNewConsents = true
			this.submit()
		}
	}
}
</script>
